import React, { ReactElement } from 'react';
import composeFp from 'lodash/fp/compose';
import { NextSeo } from 'next-seo';
import { logVisit } from '../../../../services/visits/logVisit';
import { withApolloData } from '../../../../services/apolloClient/withApolloData';
import { findTerritoryBySlug } from '../../../../components/Territory/queries/findTerritoryBySlug';
import { NextPageWithLayout } from '../../../_app';
import { TerritoryPageLayout } from '../../../../layouts/TerritoryPageLayout/TerritoryPageLayout';
import { behindFeatureFlag } from '../../../../components/FeatureFlags/getServerSidePropsDecorators/behindFeatureFlag';
import { getApolloServerClient, getCookiesForApolloServerClient } from '../../../../services/apolloClient/server';
import { TerritorySelectedTab } from '../../../../components/TerritoryTabSection/constants';
import { TerritoryExploreAllSections } from '../../../../components/TerritoryContent/TerritoryExploreAllSections';
import { TerritoryActiveMainFilter } from '../../../../components/TerritoryFilter/constants';

enum TerritoryVersionType {
  V1 = 'v1',
  V2 = 'v2',
}

type TExplorePageProps = {
  territoryName: string;
  territoryId: string;
  territorySlug: string;
  territoryVersion: TerritoryVersionType;
};

const ExplorePage: NextPageWithLayout<TExplorePageProps> = ({
  territoryName,
  territoryId,
  territorySlug,
  territoryVersion,
}) => (
  <>
    <NextSeo title={territoryName} />
    <TerritoryExploreAllSections
      territoryId={territoryId}
      territorySlug={territorySlug}
      territoryVersion={territoryVersion}
    />
  </>
);
ExplorePage.getLayout = (page: ReactElement) => {
  const {
    territoryId,
    territoryName,
    initialTerritoryTab,
    initialTerritoryExploreFilter,
    territorySlug,
    territoryVersion,
  } = page.props;

  return (
    <TerritoryPageLayout
      territoryId={territoryId}
      territoryName={territoryName}
      initialTerritoryTab={initialTerritoryTab}
      territoryVersion={territoryVersion}
      territorySlug={territorySlug}
      initialTerritoryExploreFilter={initialTerritoryExploreFilter}
      showExplorePageFilters
    >
      {page}
    </TerritoryPageLayout>
  );
};

export default ExplorePage;

export const getServerSideProps = composeFp(
  behindFeatureFlag('territoryPageV2'),
  withApolloData(ExplorePage),
  logVisit,
)(async ({ req, query }) => {
  const apolloClient = getApolloServerClient({
    cookie: getCookiesForApolloServerClient(req.headers.cookie),
  });

  const territoryRequest = await apolloClient.query({
    query: findTerritoryBySlug,
    variables: { territorySlug: query.territorySlug },
  });

  if (!territoryRequest.data.territory) {
    return {
      notFound: true,
    };
  }
  const {
    id: territoryId,
    name: territoryName,
    slug: territorySlug,
    version: territoryVersion,
  } = territoryRequest.data.territory;
  return {
    props: {
      territoryId,
      territoryName,
      territorySlug,
      territoryVersion,
      initialTerritoryTab: TerritorySelectedTab.explore,
      initialTerritoryExploreFilter: TerritoryActiveMainFilter.all,
    },
  };
});
